import React, { useState, useEffect } from 'react';
import '../styles/Insights.css';

export default function Insights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    Promise.all([
      fetch('/api/dashboard', { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
      fetch('/api/despesas', { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
      fetch('/api/dividas', { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
    ])
      .then(([dash, desp, div]) => {
        setData({ dashboard: dash.summary, despesas: desp.despesas || [], dividas: div.dividas || [] });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Analisando seus dados...</div>;
  if (!data) return <div className="error">Erro ao carregar dados</div>;

  const { dashboard, despesas, dividas } = data;

  // Calcular insights
  const despesasFixas = despesas.filter(d => d.tipo === 'fixa').reduce((sum, d) => sum + parseFloat(d.valor || 0), 0);
  const despesasVariaveis = despesas.filter(d => d.tipo === 'variavel').reduce((sum, d) => sum + parseFloat(d.valor || 0), 0);
  const porcentagemDespesasFixas = dashboard.totalDespesa > 0 ? ((despesasFixas / dashboard.totalDespesa) * 100).toFixed(1) : '0.0';

  // Categorias com mais gasto
  const categoriasGastos = {};
  despesas.forEach(d => {
    categoriasGastos[d.categoria] = (categoriasGastos[d.categoria] || 0) + parseFloat(d.valor || 0);
  });
  const top3Categorias = Object.entries(categoriasGastos)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  // Recomendações
  const recomendacoes = [];
  if (despesasVariaveis > despesasFixas) {
    recomendacoes.push({
      icon: '📊',
      titulo: 'Reduza Gastos Variáveis',
      descricao: `Suas despesas variáveis (R$ ${despesasVariaveis.toFixed(2)}) excedem as fixas. Considere estabelecer limites de gastos.`,
      impacto: Math.round((despesasVariaveis * 0.1))
    });
  }

  if (dividas.length > 0) {
    const totalDividaJuros = dividas.reduce((sum, d) => sum + (parseFloat(d.valor) * parseFloat(d.taxa_juros) / 100), 0);
    recomendacoes.push({
      icon: '💳',
      titulo: 'Priorize Dívidas',
      descricao: `Você paga ~R$ ${totalDividaJuros.toFixed(2)}/mês em juros. Quitar dívidas pode economizar isso.`,
      impacto: Math.round(totalDividaJuros)
    });
  }

  if (top3Categorias.length > 0) {
    const maiorCategoria = top3Categorias[0];
    recomendacoes.push({
      icon: '🎯',
      titulo: `Controle: ${maiorCategoria[0]}`,
      descricao: `Você gasta R$ ${maiorCategoria[1].toFixed(2)} em ${maiorCategoria[0]}. Reduza 10-15% para economizar.`,
      impacto: Math.round(maiorCategoria[1] * 0.15)
    });
  }

  return (
    <div className="insights-page">
      <h1>💡 Insights Financeiros</h1>

      {/* Cards de Resumo */}
      <div className="insights-grid">
        <div className="insight-card primary">
          <h3>Despesas Fixas</h3>
          <p className="value">R$ {despesasFixas.toFixed(2)}</p>
          <p className="percentage">{porcentagemDespesasFixas}% do total</p>
        </div>
        <div className="insight-card warning">
          <h3>Despesas Variáveis</h3>
          <p className="value">R$ {despesasVariaveis.toFixed(2)}</p>
          <p className="percentage">{(100 - porcentagemDespesasFixas).toFixed(1)}% do total</p>
        </div>
        <div className="insight-card danger">
          <h3>Dívidas Ativas</h3>
          <p className="value">{dividas.length}</p>
          <p className="percentage">Total: R$ {dashboard.totalDivida.toFixed(2)}</p>
        </div>
      </div>

      {/* Top Categorias */}
      <div className="top-categorias">
        <h2>🏆 Top Categorias de Gastos</h2>
        <div className="categoria-list">
          {top3Categorias.map((cat, idx) => (
            <div key={idx} className="categoria-item">
              <span className="rank">#{idx + 1}</span>
              <span className="nome">{cat[0]}</span>
              <span className="valor">R$ {cat[1].toFixed(2)}</span>
              <div className="bar" style={{ width: `${(cat[1] / dashboard.totalDespesa * 100)}%` }}></div>
            </div>
          ))}
        </div>
      </div>

      {/* Recomendações */}
      <div className="recomendacoes">
        <h2>📋 Recomendações para Economizar</h2>
        <div className="rec-grid">
          {recomendacoes.map((rec, idx) => (
            <div key={idx} className="rec-card">
              <div className="rec-icon">{rec.icon}</div>
              <h3>{rec.titulo}</h3>
              <p>{rec.descricao}</p>
              <div className="rec-impact">
                <span>Economia potencial:</span>
                <strong>R$ {rec.impacto.toFixed(2)}/mês</strong>
              </div>
            </div>
          ))}
        </div>
      </div>

      {recomendacoes.length === 0 && (
        <div className="no-recomendacoes">
          <p>✓ Parabéns! Sua situação financeira está equilibrada. Continue assim!</p>
        </div>
      )}
    </div>
  );
}
